import { forwardRef, ReactNode } from 'react';
import {
  FormField,
  TextInput,
  InputStatusMessage,
  GDSTextInputProps,
} from '@leagueplatform/genesis-core';
import { mergeInputCss } from './text-form-field.utils';

export interface TextFormFieldProps
  extends Omit<GDSTextInputProps, 'inputStatus' | 'ref'> {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
}

/**
 * Reusable text form field: a Genesis `FormField` wrapping a `TextInput`, with
 * error handling and the app's default rounded-input styling baked in. All
 * remaining props (`value`, `onChange`, `type`, `placeholder`, `leadingContent`,
 * `css`, …) pass straight through to the underlying `TextInput`.
 */
export const TextFormField = forwardRef<HTMLInputElement, TextFormFieldProps>(
  ({ id, label, name, required, hint, error, css, ...inputProps }, ref) => (
    <FormField
      id={id}
      label={label}
      name={name}
      hint={hint}
      required={required}
      inputStatus={error ? 'error' : undefined}
      statusMessage={
        error ? <InputStatusMessage hideIcon>{error}</InputStatusMessage> : null
      }
    >
      <TextInput {...inputProps} ref={ref} css={mergeInputCss(css)} />
    </FormField>
  ),
);

TextFormField.displayName = 'TextFormField';
